// @spec AST-STR-012
// @spec AST-STR-015
'use client';

import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { useCallback } from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useControl } from '@/features/controls/api/use-controls';
import { ControlAssetsList } from '@/features/controls/components/ControlAssetsList';
import { ControlCommentsSection } from '@/features/controls/components/ControlCommentsSection';
import { ControlDetailHeader } from '@/features/controls/components/ControlDetailHeader';
import { ControlDetailMetadata } from '@/features/controls/components/ControlDetailMetadata';
import { ControlDetailSkeleton } from '@/features/controls/components/ControlDetailSkeleton';
import { ControlLinkedItemsSection } from '@/features/controls/components/ControlLinkedItemsSection';
import { RegulationMappingEditor } from '@/features/controls/components/RegulationMappingEditor';
import type { RegulationMapping } from '@/features/controls/types';
import { useUpdateControl } from '@/features/controls/api/use-update-control';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/controls/$controlId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.controlDetails'),
  }),
  component: ControlDetailPage,
});

/**
 * Control detail page displaying complete control information.
 */
function ControlDetailPage() {
  const { t } = useTranslation(['controls', 'common']);
  const navigate = useNavigate();
  const { controlId } = useParams({ from: '/_authenticated/controls/$controlId' });
  const { organizations, isLoading: orgsLoading } = useOrganizations();

  const activeOrg = organizations.find((org) => org.isActive);

  const {
    data: control,
    isLoading: controlLoading,
    isError,
    error,
    refetch,
  } = useControl({
    orgId: activeOrg?.id ?? '',
    controlId,
    enabled: Boolean(activeOrg),
  });

  const updateControl = useUpdateControl({
    onSuccess: () => {
      toast.success(t('controls:toast.regulationMappingsUpdated'));
      void refetch();
    },
    onError: (err) => {
      toast.error(err.message || i18n.t('controls:toast.failedToUpdateMappings'));
    },
  });

  const handleSaveMappings = useCallback(
    (mappings: RegulationMapping[]) => {
      if (!control) return;

      updateControl.mutate({
        controlId: control.id,
        data: {
          regulationMappings: mappings.map((m) => ({
            regulation: m.regulation,
            article: m.article,
            description: m.description ?? null,
          })),
        },
      });
    },
    [control, updateControl]
  );

  // Loading state while checking organizations
  if (orgsLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <ControlDetailSkeleton />
        </div>
      </div>
    );
  }

  // No active organization
  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('common:routeErrors.noOrgSelected')}</AlertTitle>
            <AlertDescription>
              {t('common:routeErrors.noOrgSelectedDesc')}
            </AlertDescription>
          </Alert>
          <Button
            variant="outline"
            onClick={() => void navigate({ to: '/settings/organizations' })}
            className="mt-4"
          >
            {t('common:routeErrors.goToOrganizations')}
          </Button>
        </div>
      </div>
    );
  }

  // Loading state
  if (controlLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl">
          <ControlDetailSkeleton />
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    const is404 = (error as { status?: number } | null)?.status === 404;
    const is403 = (error as { status?: number } | null)?.status === 403;

    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>
              {is404 ? t('detailPage.controlNotFound') : is403 ? t('detailPage.accessDenied') : t('detailPage.error')}
            </AlertTitle>
            <AlertDescription>
              {is404
                ? t('detailPage.controlNotFoundDesc')
                : is403
                  ? t('detailPage.accessDeniedDesc')
                  : t('detailPage.errorLoadingDetails')}
            </AlertDescription>
          </Alert>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/controls">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('detailPage.backToControls')}
              </Link>
            </Button>
            {!is404 && !is403 && (
              <Button variant="outline" onClick={() => void refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                {t('detailPage.retry')}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // No control data
  if (!control) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-6xl space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('detailPage.controlNotFound')}</AlertTitle>
            <AlertDescription>{t('detailPage.controlDoesNotExist')}</AlertDescription>
          </Alert>
          <Button variant="outline" asChild>
            <Link to="/controls">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('detailPage.backToControls')}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Breadcrumb */}
        <nav
          className="flex items-center gap-1 text-sm text-muted-foreground"
          aria-label="Breadcrumb"
        >
          <Link to="/controls" className="transition-colors hover:text-foreground">
            {t('detailPage.breadcrumbControls')}
          </Link>
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
          <span className="font-medium text-foreground">{control.controlCode}</span>
        </nav>

        {/* Back Button */}
        <Button variant="ghost" size="sm" asChild>
          <Link to="/controls">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('detailPage.backToControls')}
          </Link>
        </Button>

        {/* Header Section */}
        <ControlDetailHeader control={control} />

        {/* Metadata Section */}
        <ControlDetailMetadata control={control} />

        {/* Linked Items Summary */}
        <ControlLinkedItemsSection control={control} />

        {/* Comments */}
        <ControlCommentsSection controlId={control.id} />

        {/* Regulation Mappings Section with Edit Capability */}
        <RegulationMappingEditor
          controlId={control.id}
          mappings={control.regulationMappings}
          isStandard={control.isStandard}
          onSave={handleSaveMappings}
        />

        {/* Assets Section */}
        <ControlAssetsList
          controlId={control.id}
          assetCount={control.assetCount}
          implementationSummary={control.implementationSummary}
        />
      </div>
    </div>
  );
}
